import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CampaignsService } from '../../../../../services/campaign/campaigns-api.service';
import { CampaignDetails, CampaignStats } from '../../types/campaignDetails';
import { GroupMembersListComponent } from '../../../../../components/organization/campaigns/group-members-list/group-members-list.component';
import { formatCount } from '../../../../../../utils/sms';
import { listJoin, plural } from '../campaign-sends';

type GroupKind = 'organization' | 'tag' | 'segment';

interface RecipientGroup {
  kind: GroupKind;
  /** Uuid of the organization, tag or segment: what the members list is read by. */
  uuid: string;
  name: string;
}

const KIND_LABEL: Record<GroupKind, string> = {
  organization: 'Organization',
  tag: 'Tag',
  segment: 'Segment',
};

/** Who the campaign goes to: its organizations, tags and segments, and their members. */
@Component({
  selector: 'app-recipients',
  standalone: true,
  imports: [MatTooltipModule, GroupMembersListComponent],
  templateUrl: './recipients.component.html',
  styleUrl: './recipients.component.css',
})
export class RecipientsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  campaignsService = inject(CampaignsService);
  campaign!: CampaignDetails;
  campaignStats!: CampaignStats;

  groups: RecipientGroup[] = [];
  selected: RecipientGroup | null = null;
  showMembersList = false;

  readonly formatCount = formatCount;
  readonly kindLabel = KIND_LABEL;

  get loaded(): boolean {
    return !!this.campaign?.id;
  }

  get statsReady(): boolean {
    return this.campaignStats?.recipientsCount !== undefined && this.campaignStats?.recipientsCount !== null;
  }

  /** "4,820 recipients in 2 organizations, 3 tags and 1 segment." */
  get lead(): string {
    const count = (kind: GroupKind) => this.groups.filter((group) => group.kind === kind).length;
    const parts = [
      count('organization') ? plural(count('organization'), 'organization', 'organizations') : '',
      count('tag') ? plural(count('tag'), 'tag', 'tags') : '',
      count('segment') ? plural(count('segment'), 'segment', 'segments') : '',
    ];
    const where = listJoin(parts);
    const who = this.statsReady
      ? plural(this.campaignStats.recipientsCount, 'recipient', 'recipients')
      : 'Recipients';
    return where ? `${who} in ${where}.` : `${who}.`;
  }

  openMembers(group: RecipientGroup) {
    if (!group.uuid) return;
    this.selected = group;
    this.showMembersList = true;
  }

  ngOnInit() {
    //assign data to component campaign variable
    this.campaignsService.getCampaignDetails
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.campaign = data as CampaignDetails;
        this.groups = this.toGroups(this.campaign);
      });

    // assign data to component campaignStat variable
    this.campaignsService.campaignStats
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.campaignStats = data as CampaignStats;
      });
  }

  /**
   * The service maps recipients to uuid + name without ids, so groups are
   * told apart (and their members read) by uuid.
   */
  private toGroups(campaign: CampaignDetails): RecipientGroup[] {
    if (!campaign?.id) return [];
    const byName = (a: RecipientGroup, b: RecipientGroup) => a.name.localeCompare(b.name);
    const organizations = (campaign.engagementOrganizations ?? [])
      .map((group) => ({ kind: 'organization' as const, uuid: group?.organizationId ?? '', name: group?.name ?? '' }))
      .sort(byName);
    const tags = (campaign.engagementRecipientTags ?? [])
      .map((group) => ({ kind: 'tag' as const, uuid: group?.tagUuid ?? '', name: group?.name ?? '' }))
      .sort(byName);
    const segments = (campaign.engagementRecipientSegments ?? [])
      .map((group) => ({ kind: 'segment' as const, uuid: group?.segmentUuid ?? '', name: group?.name ?? '' }))
      .sort(byName);
    return [...organizations, ...tags, ...segments];
  }
}
